import React, { useMemo } from 'react';

import { Box, Stack, Typography } from '@mui/material';

import { useGetDatesQuery } from 'state/apiSlice';

import { displayFriendlyDate, parseDate } from 'features/common/datetimeUtils';
import { useSubscription } from 'features/common/hooks/useSubscription';
import { classNames } from 'features/common/utils';

import { useUprStatus } from '../hooks';
import SpecialDateRequestTable from './SpecialDateRequestTable';
import { useDatesQuota } from './hooks';

import styles from './SpecialDateRequest.module.css';

export default function SpecialDateRequestPanel() {
  return (
    <Stack className={styles.wrapper}>
      <QuotaBar />
      <InfoBox />
      <SpecialDateRequestTable />
    </Stack>
  );
}

function QuotaBar() {
  const { freeQuota, usedQuota, allQuota } = useDatesQuota();

  const width = useMemo(() => {
    if (!allQuota || allQuota <= 0) return 0;
    const value = Math.floor(((allQuota - freeQuota) / allQuota) * 100);
    return value;
  }, [freeQuota, allQuota]);

  const quotaCaption = useMemo(() => {
    if (freeQuota === allQuota) return `${freeQuota} days available`;
    else
      return `${usedQuota} day${usedQuota === 1 ? '' : 's'} of ${allQuota} used`;
  }, [freeQuota, allQuota, usedQuota]);

  return (
    <Stack>
      <Box className={styles.quotaBarsHead}>
        <Typography variant="body2">Quota</Typography>
        <Typography variant="body2">{quotaCaption}</Typography>
      </Box>
      <Box className={styles.quotaBars}>
        <div
          className={classNames(styles.quotaBar, styles.consumed)}
          style={{ width: width ? `${width}%` : `8px` }}
        />
        <div className={classNames(styles.quotaBar, styles.free)} />
      </Box>
    </Stack>
  );
}

function InfoBox() {
  const { subscription, project_data } = useSubscription();
  // no project_data for new projects
  const numDaysRolling =
    project_data?.num_days_rolling ??
    subscription?.template?.num_days_rolling ??
    0;
  const { isNew, isEditable } = useUprStatus();
  const projectId = project_data?.project_id;
  const { data: datesInfo } = useGetDatesQuery(projectId!, {
    skip: !projectId,
  });

  const lastDate = useMemo(() => {
    if (!datesInfo || datesInfo.length === 0) return null;
    return datesInfo.at(-1)?.date;
  }, [datesInfo]);

  const range = useMemo(() => {
    if (!lastDate) return null;
    const to = parseDate(lastDate);
    const start = to.subtract(numDaysRolling, 'day');
    return `${displayFriendlyDate(start)} - ${displayFriendlyDate(to)}`;
  }, [numDaysRolling, displayFriendlyDate, lastDate]);

  const isUpr = isNew || isEditable;

  return (
    <Box className={styles.infoBox}>
      <Typography>Near Realtime Data {!isUpr && 'Available for'}</Typography>
      <Typography
        sx={{
          minHeight: 18,
        }}
      >
        {isUpr
          ? `Once your project is active, it will include a rolling window of the
        most recent ${numDaysRolling} days of Near Real Time data.`
          : range}
      </Typography>
    </Box>
  );
}
